import { useCallback, useEffect, useMemo, useState } from "react";

import { faSearch } from "@fortawesome/pro-regular-svg-icons";
import {
  ColumnDef,
  ColumnOrderState,
  createColumnHelper,
  DisplayColumnDef,
  OnChangeFn,
} from "@tanstack/react-table";
import { isFunction } from "lodash-es";

import {
  Cell,
  ColumnVisibilityDropdown,
  TableComp,
} from "src/base-components/Table";
import { TabData, Tabs } from "src/base-components/Tabs";
import { CaseBlock, CaseBlockProps } from "src/cases/caseView/CaseBlock";
import { PinButton } from "src/cases/common/PinButton";
import { RelatedBlockTabLabel } from "src/cases/common/RelatedBlockTabLabel";
import { useUpdateBlockConfig } from "src/cases/queries/cases";
import {
  CaseEnvironment,
  PinnedEntity,
  RelatedEntitiesCaseBlockConfigAttributes,
} from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";
import { buildEntityColumns } from "src/entities/entityView/RelatedEntities";
import { findSchema, getFixedProperties } from "src/entities/entityView/utils";
import {
  Cardinality,
  EntitySchemaProperty,
  EntitySchemaResource,
  FlatEntityResource,
  SchemaRelationship,
  useEntitySchemas,
  useRelatedEntitiesByRelationship,
} from "src/entities/queries";
import { useEntityColumnOrder } from "src/entities/useEntityColumnOrder";
import { useEnrichedEntitiesWithRelationNames } from "src/entities/useEntityDisplayValues";
import { useTrackEntityColumnCustomization } from "src/entities/useTrackEntityColumnCustomization";
import { Environment } from "src/hooks/useEnvironment";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { CasePageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

const helper = createColumnHelper<FlatEntityResource>();

const getRelationshipKey = (rel: SchemaRelationship) =>
  `${rel.from_schema}__${rel.property_name}`;

type RelatedEntitiesBlockProps = Omit<CaseBlockProps, "children"> & {
  data: RelatedEntitiesCaseBlockConfigAttributes;
  caseEnv: CaseEnvironment;
};

type EntityTabContentProps = {
  relationship: SchemaRelationship;
  entityId: string;
  environment: "live" | "sandbox";
  entitySchema: EntitySchemaResource;
  fromSchema: EntitySchemaResource;
  targetSchema: EntitySchemaResource;
  pinnedEntities: PinnedEntity[];
  onEntityPinChange: (entityId: string, pinned: boolean) => void;
};

type ColumnOrderOptions = {
  hasCustomOrder: boolean;
  onResetOrder: () => void;
  onVisibilityToggle: (columnId: string, visible: boolean) => void;
};

const buildEntityColumnsWithPinAction = (
  schema: EntitySchemaResource,
  pinnedEntities: PinnedEntity[],
  onEntityPinChange: (entityId: string, pinned: boolean) => void,
  environment: Environment,
  columnOrder: ColumnOrderOptions,
): ColumnDef<FlatEntityResource>[] => {
  const baseColumns = buildEntityColumns(schema, environment, {
    stickyIdColumn: true,
  });
  const entityColumns = baseColumns.filter(
    (col: DisplayColumnDef<FlatEntityResource, unknown>) =>
      col.id !== "properties.options",
  );

  const pinColumn = helper.display({
    id: "pin",
    header: (info) => (
      <div className="flex h-8 items-center justify-center bg-white">
        <ColumnVisibilityDropdown
          footerHint={
            columnOrder.hasCustomOrder
              ? "You have a custom column order. Reset also restores the default order"
              : undefined
          }
          min={3}
          table={info.table}
          onReset={() => {
            info.table.resetColumnVisibility();
            columnOrder.onResetOrder();
          }}
          onVisibilityToggle={columnOrder.onVisibilityToggle}
        />
      </div>
    ),
    size: 54,
    minSize: 54,
    maxSize: 54,
    meta: {
      sticky: "right",
    },
    cell: (info) => {
      const entityId = info.row.original._id;
      const isPinned = pinnedEntities.some((e) => e.id === entityId);

      return (
        <Cell
          className="items-center justify-center group-hover:bg-gray-50"
          info={info}
        >
          <PinButton
            dataLoc={`entity-${entityId}`}
            pinned={isPinned}
            onChange={(pinned) => onEntityPinChange(entityId, pinned)}
          />
        </Cell>
      );
    },
  });

  return [...entityColumns, pinColumn];
};

const EntityTabContent: React.FC<EntityTabContentProps> = ({
  relationship,
  entityId,
  environment,
  entitySchema,
  fromSchema,
  targetSchema,
  pinnedEntities,
  onEntityPinChange,
}) => {
  const { workspace } = useWorkspaceContext();
  const [activeEntityId, setActiveEntityId] = useState<string | null>(null);

  const isOutbound = entitySchema === fromSchema;
  const relatedSchema = isOutbound ? targetSchema : fromSchema;

  const { orderedPropertyKeys, hasCustomOrder, setOrder, resetOrder } =
    useEntityColumnOrder(relatedSchema);

  const { trackColumnsReordered, trackFieldVisibilityChanged } =
    useTrackEntityColumnCustomization(
      relatedSchema._id,
      "case_related_entities",
    );

  const handleVisibilityToggle = useCallback(
    (columnId: string, visible: boolean) => {
      trackFieldVisibilityChanged(columnId, !visible);
    },
    [trackFieldVisibilityChanged],
  );

  const filteredPinnedEntities = useMemo(
    () =>
      relatedSchema
        ? pinnedEntities.filter((e) => e.entity_type === relatedSchema._id)
        : [],
    [pinnedEntities, relatedSchema],
  );

  const {
    data: relatedEntitiesData,
    isLoading: isLoadingRelatedEntities,
    fetchNextPage,
  } = useRelatedEntitiesByRelationship({
    schema: entitySchema._id,
    entityId,
    env: environment,
    baseUrl: workspace.base_url ?? "",
    relationship,
  });

  const entities = useMemo(
    () =>
      (relatedEntitiesData?.pages?.flatMap(
        (page) => page.entities,
      ) as FlatEntityResource[]) || [],
    [relatedEntitiesData?.pages],
  );

  // Populates _fk_pp so relation columns show display names instead of raw IDs
  const enrichedEntities = useEnrichedEntitiesWithRelationNames({
    entities,
    schema: relatedSchema,
    environment,
  });

  const columns = useMemo(() => {
    if (!relatedSchema) return [];
    return buildEntityColumnsWithPinAction(
      relatedSchema,
      filteredPinnedEntities,
      onEntityPinChange,
      environment,
      {
        hasCustomOrder,
        onResetOrder: resetOrder,
        onVisibilityToggle: handleVisibilityToggle,
      },
    );
  }, [
    relatedSchema,
    filteredPinnedEntities,
    onEntityPinChange,
    environment,
    hasCustomOrder,
    resetOrder,
    handleVisibilityToggle,
  ]);

  // Render order of leaf columns; property column ids are the property keys
  // here. ID, primary, and secondary property are pinned first, then the
  // reorderable properties; the unlisted pin column appends at the end
  const columnOrder = useMemo(
    () => [...getFixedProperties(relatedSchema), ...orderedPropertyKeys],
    [relatedSchema, orderedPropertyKeys],
  );

  const handleColumnOrderChange: OnChangeFn<ColumnOrderState> = (updater) => {
    const nextOrder = isFunction(updater) ? updater(columnOrder) : updater;
    const propertyKeys = new Set(orderedPropertyKeys);
    setOrder(nextOrder.filter((id) => propertyKeys.has(id)));
    trackColumnsReordered();
  };

  const hasNoEntities = !isLoadingRelatedEntities && entities.length === 0;

  if (hasNoEntities) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <EmptyState
          dataLoc={`related-entities-${relationship.property_name}-empty-state`}
          description="No related entities found"
          headline="No entities"
          icon={faSearch}
          size="compact"
        />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col p-4">
      <div className="min-h-0 flex-1">
        <TableComp
          actions={{
            getSelectedId: () => activeEntityId,
            setSelectedId: setActiveEntityId,
          }}
          columnOrder={columnOrder}
          columns={columns}
          data={enrichedEntities}
          dataLoc={`related-entities-${relationship.property_name}-table`}
          frameClassName="h-full"
          getRowId={(row) => row._id}
          isLoading={isLoadingRelatedEntities}
          rowClassName="group hover:bg-gray-50 cursor-pointer"
          rowPropsGetter={(row) => ({
            onClick: () => {
              const rowId = row.original._id;
              const isCurrentlyActive = activeEntityId === rowId;
              setActiveEntityId(isCurrentlyActive ? null : rowId);
            },
          })}
          uiStoreKey={`${workspace.id}_${relatedSchema._id}_related_entities_visible_columns`}
          variant="compact"
          enableColumnReordering
          onColumnOrderChange={handleColumnOrderChange}
          onScrolledToTheBottom={fetchNextPage}
        />
      </div>
    </div>
  );
};

export const RelatedEntitiesBlock: React.FC<RelatedEntitiesBlockProps> = ({
  data,
  caseEnv,
  ...props
}) => {
  const { workspace } = useWorkspaceContext();
  const { case_id: caseId } = useParamsDecode<CasePageParams>();
  const env = caseEnv === CaseEnvironment.LIVE ? "live" : "sandbox";
  const updateBlockConfigMutation = useUpdateBlockConfig();

  const { data: schemas, isLoading: isLoadingSchemas } = useEntitySchemas({
    baseUrl: workspace.base_url ?? "",
    options: { enabled: !!workspace.base_url },
  });

  const [selectedTab, setSelectedTab] = useState<string>("");

  const relationships = useMemo(() => {
    if (!schemas) return [];

    const currentSchema = findSchema(data.entity.type, schemas);
    if (!currentSchema) return [];

    const isRelationProperty = (property: EntitySchemaProperty): boolean =>
      property._type === "relation" && Boolean(property._rel_schema);

    const outboundRelationships = Object.entries(currentSchema.properties ?? {})
      .filter(([_, property]) =>
        isRelationProperty(property as EntitySchemaProperty),
      )
      .map(([propertyName, property]) => ({
        from_schema: currentSchema._id,
        property_name: propertyName,
        target_schema: (property as EntitySchemaProperty)._rel_schema!,
      }));

    const inboundRelationships: SchemaRelationship[] = [];
    for (const schema of schemas.entities) {
      if (schema._id === currentSchema._id) continue;

      for (const [propertyName, property] of Object.entries(
        schema.properties ?? {},
      )) {
        const prop = property as EntitySchemaProperty;
        if (
          isRelationProperty(prop) &&
          prop._rel_schema === currentSchema._id &&
          !prop._backref
        ) {
          inboundRelationships.push({
            from_schema: schema._id,
            property_name: propertyName,
            target_schema: currentSchema._id,
          });
        }
      }
    }

    return [...outboundRelationships, ...inboundRelationships];
  }, [schemas, data.entity.type]);

  useEffect(() => {
    if (relationships && relationships.length > 0 && !selectedTab) {
      setSelectedTab(getRelationshipKey(relationships[0]));
    }
  }, [relationships, selectedTab]);

  const handleEntityPinChange = useCallback(
    (entityId: string, pinned: boolean) => {
      const currentSchema = findSchema(data.entity.type, schemas);
      if (!currentSchema) return;

      const currentRelationship = relationships.find(
        (rel) => getRelationshipKey(rel) === selectedTab,
      );
      if (!currentRelationship) return;

      const relatedSchemaId =
        currentRelationship.target_schema === currentSchema._id
          ? currentRelationship.from_schema
          : currentRelationship.target_schema;

      const currentPinnedEntities = data.pinned_entities || [];
      const isAlreadyPinned = currentPinnedEntities.some(
        (e) => e.id === entityId,
      );

      let updatedEntities: PinnedEntity[];
      if (pinned) {
        updatedEntities = isAlreadyPinned
          ? currentPinnedEntities
          : [
              ...currentPinnedEntities,
              { id: entityId, entity_type: relatedSchemaId },
            ];
      } else {
        updatedEntities = currentPinnedEntities.filter(
          (e) => e.id !== entityId,
        );
      }

      updateBlockConfigMutation.mutate({
        caseId,
        blockKey: props.blockKey,
        attributes: {
          pinned_entities: updatedEntities,
        },
      });
    },
    [
      data.pinned_entities,
      data.entity.type,
      schemas,
      relationships,
      selectedTab,
      caseId,
      props.blockKey,
      updateBlockConfigMutation,
    ],
  );

  const tabs: TabData[] = useMemo(() => {
    if (!schemas || !relationships.length || data.entity.id === null) return [];

    const entityId = data.entity.id;

    return relationships
      .map((relationship) => {
        const fromSchema = findSchema(relationship.from_schema, schemas);
        const targetSchema = findSchema(relationship.target_schema, schemas);
        const entitySchema = findSchema(data.entity.type, schemas);

        if (!fromSchema || !targetSchema || !entitySchema) return null;

        const isOutbound = entitySchema === fromSchema;
        const relatedSchema = isOutbound ? targetSchema : fromSchema;
        const propertyDef = (isOutbound ? entitySchema : relatedSchema)
          .properties?.[relationship.property_name];
        const cardinality =
          propertyDef?._cardinality ?? Cardinality.ONE_TO_MANY;

        const tabLabel =
          cardinality === Cardinality.ONE
            ? relatedSchema._display_name_singular
            : relatedSchema._display_name_plural;

        const pinnedCount = data.pinned_entities?.filter(
          (e) => e.entity_type === relatedSchema._id,
        ).length;

        return {
          key: getRelationshipKey(relationship),
          label: (
            <RelatedBlockTabLabel pinnedCount={pinnedCount}>
              {tabLabel}
            </RelatedBlockTabLabel>
          ),
          content: (
            <EntityTabContent
              entityId={entityId}
              entitySchema={entitySchema}
              environment={env}
              fromSchema={fromSchema}
              pinnedEntities={data.pinned_entities || []}
              relationship={relationship}
              targetSchema={targetSchema}
              onEntityPinChange={handleEntityPinChange}
            />
          ),
        };
      })
      .filter(Boolean) as TabData[];
  }, [
    schemas,
    relationships,
    data.entity.type,
    data.entity.id,
    data.pinned_entities,
    env,
    handleEntityPinChange,
  ]);

  if (!data.entity.id) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          dataLoc="related-entities-no-entity"
          description="This block currently has no linked entity."
          headline="No entity provided"
          icon={faSearch}
          size="compact"
        />
      </CaseBlock>
    );
  }

  if (!relationships?.length && !isLoadingSchemas) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          dataLoc="related-entities-empty-state"
          description="There are no related entities found for this entity."
          headline="No related entities found"
          icon={faSearch}
          size="compact"
        />
      </CaseBlock>
    );
  }

  return (
    <CaseBlock
      {...props}
      isLoading={isLoadingSchemas}
      noPaddings={true}
      noScroll={true}
    >
      <Tabs
        containerClassName="h-full flex flex-col"
        panelClassName="flex-1 min-h-0 overflow-hidden"
        panelsClassName="h-full flex flex-col"
        selectedKey={selectedTab}
        tabClassName="px-2 h-11 flex items-center"
        tabListClassName="flex-shrink-0 flex items-center h-11 border-b border-gray-200 bg-white pl-2.5"
        tabs={tabs}
        unmountPanels={false}
        onChange={setSelectedTab}
      />
    </CaseBlock>
  );
};
